import { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useFocusEffect } from 'expo-router';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import { LoyaltyQuestStepStatus } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { primitive, spacing } from 'styles/constants';
import { SyncStorageKey, syncStorage } from 'utils/environment/sync-storage';

import { IQuestStep } from './loyalty-quest/types';

const getCompletedUserJourneyFields = () =>
  syncStorage.getObject<Record<string, string[]>>(SyncStorageKey.COMPLETED_USER_JOURNEY_FIELDS) ||
  {};

const wasCompletedFieldSeen = (loyaltyId: string, field: string) => {
  const seenFields: string[] = getCompletedUserJourneyFields()[loyaltyId] || [];

  return seenFields.includes(field);
};

const markCompletedFieldAsSeen = (loyaltyId: string, field: string) => {
  const seenFields: string[] = getCompletedUserJourneyFields()[loyaltyId] || [];

  if (!seenFields.includes(field)) {
    syncStorage.setObject(SyncStorageKey.COMPLETED_USER_JOURNEY_FIELDS, {
      ...getCompletedUserJourneyFields(),
      [loyaltyId]: [...seenFields, field],
    });
  }
};

export const UserJourneyFormLabel = ({
  label,
  step,
  inline,
  fieldName,
  fieldType,
}: {
  label: string;
  step: IQuestStep;
  inline?: boolean;
  fieldName: string;
  fieldType: 'string' | 'boolean';
}) => {
  const completed = step.status === LoyaltyQuestStepStatus.COMPLETED;
  const { loyaltyUserId } = useLoyaltyUserId();

  // Mark the field as read when leaving the page
  useFocusEffect(
    useCallback(() => {
      return () => {
        if (completed && loyaltyUserId) {
          markCompletedFieldAsSeen(loyaltyUserId, fieldName);
        }
      };
    }, [completed, fieldName, loyaltyUserId])
  );

  const hideBadge =
    !loyaltyUserId || !step.bonusPoints || wasCompletedFieldSeen(loyaltyUserId, fieldName);

  if (hideBadge) {
    return <Typography fontFamily={inline ? 'body' : 'heading'}>{label}</Typography>;
  }

  const getBadgeText = () => {
    if (completed) {
      return t('crownsEarned', { points: step.bonusPoints });
    }

    if (fieldType === 'boolean') {
      return t('activateAndEarnCrowns', {
        points: step.bonusPoints,
        defaultValue: 'Activate & Earn {points} Crowns',
      });
    }

    return t('completeAndEarnCrowns', {
      points: step.bonusPoints,
      defaultValue: 'Complete & Earn {points} Crowns',
    });
  };

  return (
    <View style={styles.container}>
      <Typography fontFamily={inline ? 'body' : 'heading'}>{label}</Typography>
      <View style={[styles.badge, completed && styles.earnedBadge]}>
        <Icon variant="bkCrown" style={styles.badgeIcon} />
        <Typography style={styles.badgeText}>{getBadgeText()}</Typography>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: spacing.$2,
    alignItems: 'center',
  },
  earnedBadge: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: primitive.$bbqBrown,
  },
  badge: {
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: primitive.$meltyYellowOpacity20,
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.$1,
    paddingVertical: 2,
    gap: spacing.$1,
  },
  badgeIcon: {
    width: primitive.fontSizes.xs,
    height: primitive.fontSizes.xs,
    fontSize: primitive.fontSizes.xs,
    lineHeight: primitive.fontSizes.xs,
  },
  badgeText: {
    fontSize: primitive.fontSizes['2xs'],
    lineHeight: primitive.fontSizes.sm,
  },
});
